import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'
// eslint-import-resolver-node (the resolver wired in .eslintrc.json) does not
// understand package.json "exports" maps, so it cannot follow @devnw's
// wildcard subpath export. The module resolves correctly at build/runtime
// (webpack, Node, ts-jest) via the "exports" field — see
// __tests__/devnw-smoke.test.tsx for the Phase 0 proof.
// eslint-disable-next-line import/no-unresolved
import AuditQueryExplorer from '@devnw/cyber-iam/authz/AuditQueryExplorer/AuditQueryExplorer'
import type { AuditEvent as IamAuditEvent, AuditQueryFilters } from '@devnw/cyber-iam/iam/types'
import { toIamAuditEvent } from '../api/auditMapping'
import { useAdminGenClient } from '../api/genClient'
import { useActiveTenant } from '../layout/TenantSwitcher'

const toRFC3339 = (local: string): string => {
  if (!local) return ''
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(local)) {
    return `${local}:00Z`
  }
  return local
}

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('lg')};
`

const PageHeading = styled.h1`
  margin: 0;
  font-size: ${({ theme }) => asUnified(theme).font.sizes.lg};
  font-weight: 600;
  color: ${({ theme }) => asUnified(theme).text.primary};
`

const MutedText = styled.p`
  margin: 0;
  color: ${({ theme }) => asUnified(theme).text.secondary};
`

/**
 * OIDCPAM Gate-6 Phase 6d: migrated off MUI onto
 * @devnw/cyber-iam/authz/AuditQueryExplorer — the filter bar, cursor-paged
 * table, and per-row evidence deep-link are all rendered by the library.
 * This page still owns the tenant-wide audit query (adminQueryAudit), the
 * wire-mapping (camelCase AuditRecord -> the library's camelCase AuditEvent
 * — see auditMapping.ts for the latent bug this fixes), and the cursor:
 * "Apply" always refetches page 1 (replacing `events`); "Load more" fetches
 * the next cursor page and APPENDS host-side (the library owns no
 * pagination state of its own).
 */
export const Audit = (): JSX.Element => {
  const { tenant } = useActiveTenant()
  const genClient = useAdminGenClient()

  const [filters, setFilters] = useState<AuditQueryFilters>({})
  const [events, setEvents] = useState<IamAuditEvent[]>([])
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const fetchPage = useCallback(
    async (
      t: string,
      f: AuditQueryFilters,
      cursor: string | undefined,
      mode: 'replace' | 'append'
    ): Promise<void> => {
      setLoading(true)
      setError(undefined)
      try {
        const result = await genClient.adminQueryAudit({
          tenantId: t,
          event: f.event,
          actor: f.actor,
          since: f.since ? toRFC3339(f.since) : undefined,
          until: f.until ? toRFC3339(f.until) : undefined,
          jti: f.jti,
          pamRequestId: f.pamRequestId,
          outcome: f.outcome,
          cursor,
        })
        const mapped = (result.records ?? []).map(toIamAuditEvent)
        setEvents((prev) => (mode === 'append' ? [...prev, ...mapped] : mapped))
        setNextCursor(result.nextCursor || undefined)
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load audit log')
      } finally {
        setLoading(false)
      }
    },
    [genClient]
  )

  useEffect(() => {
    if (tenant === 'system') return
    fetchPage(tenant, filters, undefined, 'replace')
  }, [tenant, filters, fetchPage])

  const handleApplyFilters = (f: AuditQueryFilters): void => {
    setFilters(f)
  }

  const handleLoadMore = (): void => {
    if (!nextCursor) return
    fetchPage(tenant, filters, nextCursor, 'append')
  }

  if (tenant === 'system') {
    return (
      <PageContainer>
        <MutedText>Please select a tenant to view the audit log.</MutedText>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <PageHeading>Audit Log</PageHeading>

      <AuditQueryExplorer
        testID="audit-query"
        title="Audit query"
        events={events}
        onApplyFilters={handleApplyFilters}
        hasMore={Boolean(nextCursor)}
        onLoadMore={handleLoadMore}
        evidenceHref={(pamRequestId) => `/admin/audit-evidence/${pamRequestId}`}
        loading={loading}
        error={error}
      />
    </PageContainer>
  )
}
